// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import { createId } from '../../utils/id'
import { BUILT_IN_COLLECTION_NAMES, COLLECTION_CLP_OVERRIDES, DEFAULT_CLP } from './clpSeeds'
import type { ClassLevelPermissions } from './clpSeeds'

export interface SeedBuiltInCLPResult {
  total: number
  created: number
  updated: number
  skipped: number
}

interface SchemasLike {
  find(query: { selector: Record<string, unknown> }): {
    exec(): Promise<
      Array<{
        id: string
        classLevelPermissions?: ClassLevelPermissions
        update?: (patch: { $set: Record<string, unknown> }) => Promise<unknown>
        [key: string]: unknown
      }>
    >
  }
  update?: (id: string, patch: Record<string, unknown>) => Promise<unknown>
  upsert(doc: object): Promise<unknown>
}

/**
 * Idempotent: seeds a default CLP schema record for each built-in collection.
 * Skips collections that already have a builtIn schema record.
 *
 * These are marker records — they carry no field definitions. Their sole purpose
 * is to attach a {@link ClassLevelPermissions} object to each native RxDB
 * collection so the RBAC enforcement layer has something to evaluate against.
 *
 * @param schemasCollection - RxDB schemas collection or a compatible mock.
 * @param orgId - The organisation to seed CLP records for.
 * @param clp - The CLP to apply; defaults to {@link DEFAULT_CLP}.
 * @returns Counts of created, skipped, and total records processed.
 */
export async function seedBuiltInCLP(
  schemasCollection: SchemasLike,
  orgId: string,
  clp: ClassLevelPermissions = DEFAULT_CLP
): Promise<SeedBuiltInCLPResult> {
  let created = 0
  let updated = 0
  let skipped = 0

  for (const collectionName of BUILT_IN_COLLECTION_NAMES) {
    const effectiveCLP = COLLECTION_CLP_OVERRIDES[collectionName]
      ? { ...clp, ...COLLECTION_CLP_OVERRIDES[collectionName] }
      : clp

    const results = await schemasCollection
      .find({ selector: { name: collectionName, orgId, builtIn: true } })
      .exec()

    if (results.length > 0) {
      const existing = results[0]
      if (
        existing &&
        shouldUpgradeBuiltInCLP(collectionName, existing.classLevelPermissions, effectiveCLP, clp)
      ) {
        await updateExistingBuiltInCLP(schemasCollection, existing, effectiveCLP)
        updated++
        continue
      }
      skipped++
      continue
    }

    const now = new Date().toISOString()
    await schemasCollection.upsert({
      id: createId('schemas'),
      name: collectionName,
      title: collectionName.charAt(0).toUpperCase() + collectionName.slice(1),
      description: `Built-in collection: ${collectionName}`,
      category: 'system',
      // 'records' is required by SchemaModel but has no semantic meaning here — these are
      // CLP marker records for native collections, not VCA-backed virtual collections.
      target: 'records',
      titleField: 'title',
      orgId,
      builtIn: true,
      version: 1,
      archived: false,
      fields: [],
      classLevelPermissions: effectiveCLP,
      createdAt: now,
      updatedAt: now,
    })
    created++
  }

  return { total: BUILT_IN_COLLECTION_NAMES.length, created, updated, skipped }
}

function shouldUpgradeBuiltInCLP(
  collectionName: string,
  existingCLP: ClassLevelPermissions | undefined,
  effectiveCLP: ClassLevelPermissions,
  defaultCLP: ClassLevelPermissions
): boolean {
  if (!COLLECTION_CLP_OVERRIDES[collectionName]) return false
  if (deepEqual(existingCLP, effectiveCLP)) return false
  return existingCLP === undefined || deepEqual(existingCLP, defaultCLP)
}

async function updateExistingBuiltInCLP(
  schemasCollection: SchemasLike,
  existing: {
    id: string
    update?: (patch: { $set: Record<string, unknown> }) => Promise<unknown>
    [key: string]: unknown
  },
  classLevelPermissions: ClassLevelPermissions
): Promise<void> {
  const patch = {
    classLevelPermissions,
    updatedAt: new Date().toISOString(),
  }

  if (schemasCollection.update) {
    await schemasCollection.update(existing.id, patch)
    return
  }

  if (existing.update) {
    await existing.update({ $set: patch })
    return
  }

  await schemasCollection.upsert({ ...existing, ...patch })
}

function deepEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}
